import { memo } from 'react';
import { ScrollView } from 'react-native';
import { YStack } from 'tamagui';
import { H4 } from 'tamagui';
import { Card } from 'tamagui';
import { H6 } from 'tamagui';
import { View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SizableText } from 'tamagui';
import { Text } from 'tamagui';
import { XStack } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { Platform } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import moment from 'moment';

interface DashboardTabProps {
  data: any[];
  theme: boolean;
  totalPosts: number;
}

const DashboardTab = memo(({
  data,
  theme,
  totalPosts,
}: DashboardTabProps) => {
  // Calculate stats from data
  const totalViews = data?.reduce((sum, post) => sum + (post.viewCount || 0), 0) || 0;
  const totalLikes = data?.reduce((sum, post) => sum + (post.likes?.length || 0), 0) || 0;
  const totalComments = data?.reduce((sum, post) => sum + (post.comments?.length || 0), 0) || 0;
  const totalShares = data?.reduce((sum, post) => sum + (post.shares?.length || 0), 0) || 0;
  const avgEngagement = totalPosts > 0 ? ((totalLikes + totalComments + totalShares) / totalPosts).toFixed(1) : '0';

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: 20, gap: 20 }}
      showsVerticalScrollIndicator={false}
    >
      <YStack gap={20}>
        <H4 style={{ color: theme ? "#1e293b" : "#f1f5f9", fontWeight: '700' }}>
          Overview
        </H4>

        {/* Quick Stats Grid */}
        <XStack gap={12} flexWrap="wrap" justifyContent="space-between">
          {[
            {
              title: 'Total Posts',
              value: totalPosts.toString(),
              icon: 'photo-library',
              color: theme ? "#f57c00" : "#ffcc02",
              bgColor: theme ? "#fff3e0" : "#e65100"
            },
            {
              title: 'Total Views',
              value: totalViews.toLocaleString(),
              icon: 'visibility',
              color: theme ? "#2196f3" : "#64b5f6",
              bgColor: theme ? "#e3f2fd" : "#0d47a1"
            },
            {
              title: 'Total Likes',
              value: totalLikes.toLocaleString(),
              icon: 'favorite',
              color: theme ? "#e91e63" : "#f48fb1",
              bgColor: theme ? "#fce4ec" : "#880e4f"
            },
            {
              title: 'Total Comments',
              value: totalComments.toLocaleString(),
              icon: 'comment',
              color: theme ? "#4caf50" : "#81c784",
              bgColor: theme ? "#e8f5e8" : "#1b5e20"
            },
            {
              title: 'Total Shares',
              value: totalShares.toLocaleString(),
              icon: 'share',
              color: theme ? "#9c27b0" : "#ce93d8",
              bgColor: theme ? "#f3e5f5" : "#4a148c"
            },
            {
              title: 'Avg Engagement',
              value: avgEngagement,
              icon: 'trending-up',
              color: theme ? "#ff5722" : "#ff8a65",
              bgColor: theme ? "#fbe9e7" : "#bf360c"
            }
          ].map((stat, index) => (
            <Animatable.View
              key={index}
              animation="fadeInUp"
              delay={index * 100}
              style={{
                flexBasis: Platform.OS === 'web' ? '30%' : '48%',
                backgroundColor: stat.bgColor,
                borderRadius: 16,
                padding: 20,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
              }}
            >
              <MaterialIcons
                name={stat.icon as any}
                size={32}
                color={stat.color}
                style={{ marginBottom: 12 }}
              />
              <Text style={{
                fontSize: 24,
                fontWeight: '800',
                color: stat.color,
                marginBottom: 4
              }}>
                {stat.value}
              </Text>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: theme ? "#64748b" : "#94a3b8",
                textAlign: 'center'
              }}>
                {stat.title}
              </Text>
            </Animatable.View>
          ))}
        </XStack>

        {/* Recent Activity */}
        <Card style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 16,
          padding: 20,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}>
          <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9", marginBottom: 16 }}>
            Recent Activity
          </H6>
          {data && data.length > 0 ? (
            <YStack gap={12}>
              {data.slice(0, 5).map((post, index) => (
                <XStack key={post.id || post._id} gap={12} alignItems="center" paddingVertical={8}>
                  <View style={{
                    width: 50,
                    height: 50,
                    borderRadius: 8,
                    backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <MaterialIcons
                      name="photo-library"
                      size={24}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                  </View>
                  <YStack flex={1}>
                    <SizableText style={{
                      color: theme ? "#1e293b" : "#f1f5f9",
                      fontWeight: '600',
                      fontSize: 14
                    }}>
                      {post.title || post.content?.substring(0, 50) || 'Untitled Post'}
                    </SizableText>
                    <SizableText style={{
                      color: theme ? "#64748b" : "#94a3b8",
                      fontSize: 12
                    }}>
                      {moment(post.createdAt).format('MMM DD, YYYY')}
                    </SizableText>
                  </YStack>
                  <XStack gap={16}>
                    <View style={{ alignItems: 'center' }}>
                      <AntDesign name="eye" size={16} color={theme ? "#64748b" : "#94a3b8"} />
                      <SizableText style={{ fontSize: 10, color: theme ? "#64748b" : "#94a3b8" }}>
                        {post.viewCount || 0}
                      </SizableText>
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <AntDesign name="like" size={16} color={theme ? "#64748b" : "#94a3b8"} />
                      <SizableText style={{ fontSize: 10, color: theme ? "#64748b" : "#94a3b8" }}>
                        {post.likes?.length || 0}
                      </SizableText>
                    </View>
                  </XStack>
                </XStack>
              ))}
            </YStack>
          ) : (
            <SizableText style={{ color: theme ? "#64748b" : "#94a3b8", textAlign: 'center', paddingVertical: 20 }}>
              No recent activity
            </SizableText>
          )}
        </Card>
      </YStack>
    </ScrollView>
  );
});
export default DashboardTab;